import { makeAutoObservable } from "mobx";
import { DEFAULT_MASK_EDITOR_BRUSH_PROPS } from "../../../constants/FiboEditConstants";
import { BrushActions } from "../../../hooks/brush/brushActions";
import { BrushCanvasRefs, IBrushSettings } from "../../../models/brush";
import { DEFAULT_BRUSH } from "../../Playground/ImageToImage/Config/Features/EraseObject/eraseObjectConstants";
import { MaskType } from "./ControlPanel/ControlPanel";

export interface ImageDimensions {
	width: number;
	height: number;
}

export interface IMaskEditorStore {
	brushCanvasRefs: BrushCanvasRefs[];
	brushConfigs: IBrushSettings;
	brushSize: number;
	maskType: MaskType;
	isMaskApplied: boolean;
	isEditorOpened: boolean;
	originalImageDimensions: ImageDimensions | null;
	isMaskEditing: boolean; // If the mask is being edited (from saved mask base64 mask)

	setBrushSize: (size: number) => void;
	setMaskType: (type: MaskType) => void;
	resetBrushConfigs: () => void;
	clearCanvas: () => void;
	getMaskFileBase64: () => Promise<string>;
	setOriginalImageDimensions: (imageUrl: string) => Promise<void>;
	validateMaskAspectRatio: (maskBase64: string) => Promise<boolean>;
	loadMaskOntoCanvas: (maskBase64: string) => Promise<boolean>;
}

class MaskEditorStore implements IMaskEditorStore {
	brushCanvasRefs: BrushCanvasRefs[] = [];
	brushConfigs: IBrushSettings = {
		...DEFAULT_BRUSH,
	};
	brushSize: number = DEFAULT_MASK_EDITOR_BRUSH_PROPS.size!;
	maskType: MaskType = DEFAULT_MASK_EDITOR_BRUSH_PROPS.type!;

	isMaskApplied: boolean = false;
	isEditorOpened: boolean = false;
	originalImageDimensions: ImageDimensions | null = null;
	isMaskEditing: boolean = false; // If a mask is already exists and is being edited

	constructor() {
		makeAutoObservable(this);
	}

	setBrushSize = (size: number) => {
		this.brushSize = size;
		this.brushConfigs.lineWidth = size;
	};

	setMaskType = (type: MaskType) => {
		this.maskType = type;
		this.brushConfigs.brushMode = type;
	};

	async setOriginalImageDimensions(imageUrl: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.src = imageUrl;
			img.onload = () => {
				this.originalImageDimensions = { width: img.width, height: img.height };
				resolve();
			};
			img.onerror = (err) => reject(new Error(`Failed to load image for dimensions: ${err}`));
		});
	}

	private scaleMaskToOriginalDimensions(maskBase64: string): Promise<string> {
		return new Promise((resolve, reject) => {
			if (!this.originalImageDimensions) {
				resolve(maskBase64);
				return;
			}

			const { width: targetWidth, height: targetHeight } = this.originalImageDimensions;
			const img = new Image();
			img.src = maskBase64;

			img.onload = () => {
				// If dimensions already match, no scaling needed
				if (img.width === targetWidth && img.height === targetHeight) {
					resolve(maskBase64);
					return;
				}

				const canvas = document.createElement("canvas");
				canvas.width = targetWidth;
				canvas.height = targetHeight;

				const ctx = canvas.getContext("2d");
				if (!ctx) {
					reject(new Error("Failed to get canvas context"));
					return;
				}

				// Use nearest-neighbor interpolation for mask (preserves hard edges)
				ctx.imageSmoothingEnabled = false;
				ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

				resolve(canvas.toDataURL("image/png"));
			};

			img.onerror = (err) => reject(new Error(`Failed to load mask for scaling: ${err}`));
		});
	}

	async getMaskFileBase64(): Promise<string> {
		if (!this.brushCanvasRefs.length || !this.brushCanvasRefs[0].canvasRef) {
			return "";
		}

		const canvasRef = this.brushCanvasRefs[0].canvasRef.current;
		const canvasOverlayRef = this.brushCanvasRefs[0].canvasOverlayRef?.current;

		// Validate canvas is properly initialized with dimensions
		if (!canvasRef || !canvasOverlayRef || canvasRef.width === 0 || canvasRef.height === 0) {
			return "";
		}

		try {
			const brushActions = new BrushActions(canvasRef, canvasOverlayRef);
			const maskBase64 = await brushActions.handleCanvasDownload();

			if (!maskBase64) {
				return "";
			}

			// Scale the mask to match the original image dimensions
			return this.scaleMaskToOriginalDimensions(maskBase64);
		} catch (error) {
			console.warn("Failed to get mask from canvas:", error);
			return "";
		}
	}

	resetBrushConfigs = () => {
		this.brushConfigs.reset = true;
	};

	clearCanvas = () => {
		if (!this.brushCanvasRefs.length || !this.brushCanvasRefs[0].canvasOverlayRef?.current) {
			return;
		}

		const brushActions = new BrushActions(
			this.brushCanvasRefs[0].canvasRef.current,
			this.brushCanvasRefs[0].canvasOverlayRef.current,
		);

		brushActions.handleCanvasClear();
		this.isMaskApplied = false;
	};

	async validateMaskAspectRatio(maskBase64: string): Promise<boolean> {
		if (!this.originalImageDimensions) {
			return true; // No original dimensions to compare against
		}

		const brushActions = new BrushActions(null, null);
		return brushActions.validateMaskAspectRatio(maskBase64, this.originalImageDimensions);
	}

	async loadMaskOntoCanvas(maskBase64: string): Promise<boolean> {
		try {
			if (!this.brushCanvasRefs.length || !this.brushCanvasRefs[0].canvasOverlayRef?.current) {
				console.warn("Canvas overlay ref not ready, skipping mask load");
				return false;
			}

			const brushActions = new BrushActions(
				this.brushCanvasRefs[0].canvasRef.current,
				this.brushCanvasRefs[0].canvasOverlayRef.current,
			);

			await brushActions.loadMaskImage(maskBase64, this.brushConfigs.strokeStyle);
			this.isMaskApplied = true;
			return true;
		} catch (error) {
			console.error("Failed to load mask onto canvas", error);
			return false;
		}
	}
}

export default MaskEditorStore;
